import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import {
    NavLink,
    useParams,
} from 'react-router-dom';
import {
    Button,
    Card,
    Col,
    Container,
    Form,
    Nav,
    Row,
} from 'react-bootstrap';
import {
    Field,
    Formik,
} from 'formik';

import { useFormat } from '../../components/FormatContext';
import API from '../../api/axios';
import useCompanyContext from '../../hooks/useCompanyContext';

const TABS = [
    {
        id: 'booking',
        label: 'Booking Information',
    },
    {
        id: 'review',
        label: 'Review Information',
    },
];

const EMPTY_FORMIK_VALUES = {
    order: {
        bookingNumber: '',
        rentalOrderId: '',
        customerName: '',
        customerLastname: '',
        customerEmail: '',
        supplierName: '',
        pickupShopName: '',
        pickupShopCity: '',
        pickupShopCountry: '',
        dropoffShopName: '',
        dropoffShopCity: '',
        dropoffShopCountry: '',
        start: '',
        end: '',
        pickUpTime: '',
        dropOffTime: '',
        motorcycleName: '',
        motorcycleType: '',
        motorcycleBrand: '',
        total: 0,
        currency: '',
        status: '',
        agent: '',
        link: '',
    },

    opinion: {
        id: '',
        status: 'UNDER_REVIEW',
        rmbServiceRating: null,
        shopServiceRating: null,
        motorcycleStatusRating: null,
        valueForMoneyRating: null,
        isPassedRmb: false,
        isPassedShop: false,
        isSentToSupplier: false,
        rmbComment: '',
        shopComment: '',
        agent: '',
        createDate: '',
    },
};

const toYMD = (date) => {
    if (!date) {
        return '';
    }

    const year =
        date.getFullYear();

    const month =
        String(
            date.getMonth() + 1,
        ).padStart(
            2,
            '0',
        );

    const day =
        String(
            date.getDate(),
        ).padStart(
            2,
            '0',
        );

    return `${year}-${month}-${day}`;
};

const toFormikValues = (
    apiData,
) => {
    const order =
        apiData?.order ?? {};

    const review =
        apiData?.review ?? {};

    const startDate =
        order?.period?.startDate
            ? new Date(
                order.period.startDate,
            )
            : null;

    const endDate =
        order?.period?.endDate
            ? new Date(
                order.period.endDate,
            )
            : null;

    return {
        order: {
            bookingNumber:
                order?.bookingNumber ??
                '',

            rentalOrderId:
                order?.rentalOrderId ??
                '',

            customerName:
                order?.customer?.name ??
                '',

            customerLastname:
                order?.customer?.lastName ??
                '',

            customerEmail:
                order?.customer?.email ??
                '',

            supplierName:
                order?.pickup?.companyName ??
                order?.pickup?.supplierName ??
                '',

            pickupShopName:
                order?.pickup?.shopName ??
                '',

            pickupShopCity:
                order?.pickup?.city ??
                '',

            pickupShopCountry:
                order?.pickup?.country ??
                '',

            dropoffShopName:
                order?.dropoff?.shopName ??
                '',

            dropoffShopCity:
                order?.dropoff?.city ??
                '',

            dropoffShopCountry:
                order?.dropoff?.country ??
                '',

            start:
                toYMD(
                    startDate,
                ),

            end:
                toYMD(
                    endDate,
                ),

            pickUpTime:
                order?.period?.pickupTime ??
                '',

            dropOffTime:
                order?.period?.dropoffTime ??
                '',

            motorcycleName:
                order?.motorcycle?.name ??
                '',

            motorcycleType:
                order?.motorcycle?.type ??
                '',

            motorcycleBrand:
                order?.motorcycle?.brand ??
                '',

            total:
                typeof order?.pricing?.total ===
                    'number'
                    ? order.pricing.total
                    : 0,

            currency:
                order?.pricing?.currency ??
                '',

            status:
                order?.status ??
                '',

            agent:
                order?.agent ??
                '',

            link:
                order?.rentalOrderId
                    ? `/bookings/edit/${order.rentalOrderId}`
                    : '',
        },

        opinion: {
            id:
                review?.id ??
                '',

            status:
                review?.status ??
                'UNDER_REVIEW',

            rmbServiceRating:
                review?.ratings
                    ?.rmbService ??
                null,

            shopServiceRating:
                review?.ratings
                    ?.shopService ??
                null,

            motorcycleStatusRating:
                review?.ratings
                    ?.motorcycleStatus ??
                null,

            valueForMoneyRating:
                review?.ratings
                    ?.valueForMoney ??
                null,

            isPassedRmb:
                Boolean(
                    review?.decisions
                        ?.isPassedRmb,
                ),

            isPassedShop:
                Boolean(
                    review?.decisions
                        ?.isPassedShop,
                ),

            isSentToSupplier:
                Boolean(
                    review?.decisions
                        ?.isSentToSupplier,
                ),

            rmbComment:
                review?.comments?.rmb ??
                '',

            shopComment:
                review?.comments?.shop ??
                '',

            agent:
                order?.agent ??
                '',

            createDate:
                review?.createdAt ??
                '',
        },
    };
};

const FormSection = ({
    title,
    level = 1,
    children,
    button = null,
}) => {
    const Tag =
        `h${Math.min(
            6,
            level + 3,
        )}`;

    return (
        <section className="mb-4 form-section">
            <Tag
                className={`text-muted ${level === 1
                        ? 'fw-semibold'
                        : 'fw-medium'
                    }`}
            >
                {title} {button}
            </Tag>

            {children}
        </section>
    );
};

const BookingTab = ({
    values,
    buildCompanyUrl,
}) => {
    const {
        baseFormatters,
    } = useFormat();

    const hasStartEnd =
        values?.order?.start &&
        values?.order?.end &&
        values?.order?.pickUpTime &&
        values?.order?.dropOffTime;

    return (
        <FormSection
            title="Booking Information"
            level={2}
        >
            <Row>
                <Col
                    sm={6}
                    md={6}
                >
                    <Form.Label className="fw-semibold mb-2">
                        Booking Number:
                    </Form.Label>

                    <div className="d-flex align-items-center mb-3">
                        {
                            values.order
                                .bookingNumber
                        }
                    </div>

                    <Form.Label className="fw-semibold mb-2">
                        Customer Name:
                    </Form.Label>

                    <div className="d-flex align-items-center mb-3">
                        {
                            values.order
                                .customerName
                        }{' '}
                        {
                            values.order
                                .customerLastname
                        }
                    </div>

                    <Form.Label className="fw-semibold mb-2">
                        Customer Email:
                    </Form.Label>

                    <div className="d-flex align-items-center mb-3">
                        {
                            values.order
                                .customerEmail
                        }
                    </div>

                    <Form.Label className="fw-semibold mb-2">
                        Supplier Name:
                    </Form.Label>

                    <div className="d-flex align-items-center mb-3">
                        {
                            values.order
                                .supplierName
                        }
                    </div>

                    <Form.Label className="fw-semibold mb-2">
                        Pickup Shop:
                    </Form.Label>

                    <div className="d-flex align-items-center mb-3">
                        {
                            values.order
                                .pickupShopName
                        }

                        {values.order
                            .pickupShopCity
                            ? `, ${values.order.pickupShopCity}`
                            : ''}
                    </div>

                    <Form.Label className="fw-semibold mb-2">
                        Motorcycle:
                    </Form.Label>

                    <div className="d-flex align-items-center mb-3">
                        {
                            values.order
                                .motorcycleName
                        }

                        {values.order
                            .motorcycleType
                            ? ` (${values.order.motorcycleType})`
                            : ''}
                    </div>
                </Col>

                <Col
                    sm={6}
                    md={6}
                >
                    <Form.Label className="fw-semibold mb-2">
                        Rental Details:
                    </Form.Label>

                    <div className="d-flex flex-column mb-3">
                        {hasStartEnd ? (
                            <>
                                {baseFormatters.datetime.format(
                                    new Date(
                                        `${values.order.start}T${values.order.pickUpTime}:00`,
                                    ),
                                )}

                                {' - '}

                                {baseFormatters.datetime.format(
                                    new Date(
                                        `${values.order.end}T${values.order.dropOffTime}:00`,
                                    ),
                                )}
                            </>
                        ) : (
                            <span className="text-muted">
                                —
                            </span>
                        )}
                    </div>

                    <Form.Label className="fw-semibold mb-2">
                        Total Price:
                    </Form.Label>

                    <div className="d-flex align-items-center mb-3">
                        {values.order.currency
                            ? `${values.order.currency} `
                            : ''}

                        {Number(
                            values.order.total ||
                            0,
                        ).toFixed(
                            2,
                        )}
                    </div>

                    <Form.Label className="fw-semibold mb-2">
                        Status:
                    </Form.Label>

                    <div className="d-flex align-items-center mb-3">
                        {
                            values.order
                                .status
                        }
                    </div>

                    <Form.Label className="fw-semibold mb-2">
                        Agent:
                    </Form.Label>

                    <div className="d-flex align-items-center mb-3">
                        {
                            values.order
                                .agent
                        }
                    </div>

                    <Form.Label className="fw-semibold mb-2">
                        Booking Link:
                    </Form.Label>

                    <div className="d-flex align-items-center mb-3">
                        {values.order.link ? (
                            <NavLink
                                to={buildCompanyUrl(
                                    values.order.link,
                                )}
                            >
                                View Booking Details
                            </NavLink>
                        ) : (
                            <span className="text-muted">
                                No link available
                            </span>
                        )}
                    </div>
                </Col>
            </Row>
        </FormSection>
    );
};

const ReviewTab = ({
    values,
    setFieldValue,
    isAdmin,
}) => {
    const {
        baseFormatters,
    } = useFormat();

    return (
        <Col
            sm={12}
            md={12}
        >
            <FormSection
                title="Rentalmotorbike Review"
                level={2}
            >
                <Row>
                    <Col
                        sm={6}
                        md={6}
                    >
                        <Form.Label className="fw-semibold mb-2">
                            Service Rating:
                        </Form.Label>

                        <div className="d-flex align-items-center mb-3">
                            {values.opinion
                                .rmbServiceRating ??
                                'N/A'}
                            /5
                        </div>
                    </Col>

                    <Col
                        sm={12}
                        md={12}
                    >
                        <Form.Label className="fw-semibold mb-2">
                            Comments:
                        </Form.Label>

                        <div className="d-flex align-items-center mb-3">
                            {values.opinion
                                .rmbComment ||
                                'No comments'}
                        </div>
                    </Col>
                </Row>
            </FormSection>

            <FormSection
                title="Shop Review"
                level={2}
            >
                <Row>
                    <Col
                        sm={6}
                        md={6}
                    >
                        <Form.Label className="fw-semibold mb-2">
                            Shop Service Rating:
                        </Form.Label>

                        <div className="d-flex align-items-center mb-3">
                            {values.opinion
                                .shopServiceRating ??
                                'N/A'}
                            /5
                        </div>
                    </Col>

                    <Col
                        sm={6}
                        md={6}
                    >
                        <Form.Label className="fw-semibold mb-2">
                            Motorcycle Condition:
                        </Form.Label>

                        <div className="d-flex align-items-center mb-3">
                            {values.opinion
                                .motorcycleStatusRating ??
                                'N/A'}
                            /5
                        </div>
                    </Col>

                    <Col
                        sm={12}
                        md={12}
                    >
                        <Form.Label className="fw-semibold mb-2">
                            Shop Comments:
                        </Form.Label>

                        <div className="d-flex align-items-center mb-3">
                            {values.opinion
                                .shopComment ||
                                'No comments'}
                        </div>
                    </Col>
                </Row>
            </FormSection>

            <FormSection
                title="Review Control"
                level={2}
            >
                <Row>
                    <Col
                        sm={6}
                        md={6}
                    >
                        <Form.Label className="fw-semibold mb-2">
                            Handled by Agent:
                        </Form.Label>

                        <div className="d-flex align-items-center mb-3">
                            {
                                values.opinion
                                    .agent
                            }
                        </div>
                    </Col>

                    <Col
                        sm={6}
                        md={6}
                    >
                        <Form.Label className="fw-semibold mb-2">
                            Created Date:
                        </Form.Label>

                        <div className="d-flex align-items-center mb-3">
                            {values.opinion
                                .createDate
                                ? baseFormatters.datetime.format(
                                    new Date(
                                        values.opinion.createDate,
                                    ),
                                )
                                : '—'}
                        </div>
                    </Col>

                    {isAdmin && (
                        <>
                            <Col
                                sm={6}
                                md={6}
                            >
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="opinion.isPassedRmb"
                                        label="Publish Rentalmotorbike Review"
                                        checked={
                                            Boolean(
                                                values
                                                    .opinion
                                                    .isPassedRmb,
                                            )
                                        }
                                        onChange={(
                                            event,
                                        ) =>
                                            setFieldValue(
                                                'opinion.isPassedRmb',
                                                event
                                                    .target
                                                    .checked,
                                            )
                                        }
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="opinion.isPassedShop"
                                        label="Publish Shop Review"
                                        checked={
                                            Boolean(
                                                values
                                                    .opinion
                                                    .isPassedShop,
                                            )
                                        }
                                        onChange={(
                                            event,
                                        ) =>
                                            setFieldValue(
                                                'opinion.isPassedShop',
                                                event
                                                    .target
                                                    .checked,
                                            )
                                        }
                                    />
                                </Form.Group>
                            </Col>

                            <Col
                                sm={6}
                                md={6}
                            >
                                <Form.Label className="fw-semibold mb-2">
                                    Review Status:
                                </Form.Label>

                                <Field
                                    as="select"
                                    name="opinion.status"
                                    className="form-select mb-3"
                                >
                                    <option value="UNDER_REVIEW">
                                        Under Review
                                    </option>

                                    <option value="REJECTED">
                                        Rejected
                                    </option>

                                    <option value="APPROVED">
                                        Approved
                                    </option>
                                </Field>
                            </Col>
                        </>
                    )}
                </Row>
            </FormSection>
        </Col>
    );
};

const ReviewForm = () => {
    const { id } =
        useParams();

    const {
        isAdmin,
        buildCompanyUrl,
    } = useCompanyContext();

    const [
        activeTab,
        setActiveTab,
    ] = useState(
        'review',
    );

    const [
        loading,
        setLoading,
    ] = useState(false);

    const [
        errorMsg,
        setErrorMsg,
    ] = useState('');

    const [
        formInitialValues,
        setFormInitialValues,
    ] = useState(
        EMPTY_FORMIK_VALUES,
    );

    const currentIndex =
        TABS.findIndex(
            (tab) =>
                tab.id ===
                activeTab,
        );

    const fetchDetail =
        useCallback(async () => {
            const {
                data,
            } =
                await API.get(
                    `/rental-order-review/${id}`,
                );

            return data;
        }, [id]);

    useEffect(() => {
        if (!id) {
            return;
        }

        let cancelled =
            false;

        const load =
            async () => {
                try {
                    setLoading(
                        true,
                    );

                    setErrorMsg('');

                    const data =
                        await fetchDetail();

                    if (cancelled) {
                        return;
                    }

                    setFormInitialValues(
                        toFormikValues(
                            data,
                        ),
                    );
                } catch (error) {
                    if (cancelled) {
                        return;
                    }

                    setErrorMsg(
                        error?.response
                            ?.data
                            ?.message ||
                        error?.message ||
                        'Failed to load review',
                    );

                    setFormInitialValues(
                        EMPTY_FORMIK_VALUES,
                    );
                } finally {
                    if (
                        !cancelled
                    ) {
                        setLoading(
                            false,
                        );
                    }
                }
            };

        load();

        return () => {
            cancelled = true;
        };
    }, [
        id,
        fetchDetail,
    ]);

    const handleTabChange =
        useCallback(
            (tabId) => {
                setActiveTab(
                    tabId,
                );

                window.scrollTo({
                    top: 0,
                    behavior:
                        'smooth',
                });
            },
            [],
        );

    const handleSubmit =
        useCallback(
            async (
                values,
                {
                    setSubmitting,
                },
            ) => {
                try {
                    setSubmitting(
                        true,
                    );

                    await API.patch(
                        `/rental-order-review/${id}`,
                        {
                            isPassedRmb:
                                Boolean(
                                    values
                                        .opinion
                                        .isPassedRmb,
                                ),

                            isPassedShop:
                                Boolean(
                                    values
                                        .opinion
                                        .isPassedShop,
                                ),

                            status:
                                values
                                    .opinion
                                    .status,
                        },
                    );

                    alert(
                        'Review saved successfully',
                    );
                } catch (error) {
                    alert(
                        error?.response
                            ?.data
                            ?.message ||
                        error?.message ||
                        'Failed to save review',
                    );
                } finally {
                    setSubmitting(
                        false,
                    );
                }
            },
            [id],
        );

    return (
        <Formik
            initialValues={
                formInitialValues
            }
            enableReinitialize
            onSubmit={
                handleSubmit
            }
        >
            {({
                values,
                setFieldValue,
                handleSubmit:
                formikHandleSubmit,
                isSubmitting,
            }) => (
                <Container
                    fluid
                    className="d-flex flex-column"
                >
                    <Row className="justify-content-center">
                        <Col
                            sm={12}
                            className="mb-4"
                        >
                            <span className="fw-semibold">
                                Motorcycle
                                Information
                            </span>
                        </Col>

                        <Col sm={12}>
                            {errorMsg && (
                                <div className="alert alert-danger">
                                    {errorMsg}
                                </div>
                            )}

                            {loading && (
                                <div className="alert alert-info">
                                    Loading...
                                </div>
                            )}
                        </Col>

                        <Col
                            sm="auto"
                            style={{
                                position:
                                    'sticky',
                                top:
                                    '10px',
                                height:
                                    'fit-content',
                                alignSelf:
                                    'flex-start',
                                maxWidth:
                                    '280px',
                            }}
                        >
                            <Card>
                                <Nav
                                    variant="pills"
                                    className="flex-column p-3"
                                >
                                    {TABS.map(
                                        (tab) => (
                                            <Nav.Item
                                                key={
                                                    tab.id
                                                }
                                            >
                                                <Nav.Link
                                                    active={
                                                        activeTab ===
                                                        tab.id
                                                    }
                                                    onClick={() =>
                                                        handleTabChange(
                                                            tab.id,
                                                        )
                                                    }
                                                    className="mb-2 rounded"
                                                >
                                                    {
                                                        tab.label
                                                    }
                                                </Nav.Link>
                                            </Nav.Item>
                                        ),
                                    )}
                                </Nav>
                            </Card>
                        </Col>

                        <Col className="flex-grow-1">
                            <Card className="p-4">
                                {activeTab ===
                                    'booking' && (
                                        <BookingTab
                                            values={
                                                values
                                            }
                                            buildCompanyUrl={
                                                buildCompanyUrl
                                            }
                                        />
                                    )}

                                {activeTab ===
                                    'review' && (
                                        <ReviewTab
                                            values={
                                                values
                                            }
                                            setFieldValue={
                                                setFieldValue
                                            }
                                            isAdmin={
                                                isAdmin
                                            }
                                        />
                                    )}

                                <div className="d-flex justify-content-end mt-3">
                                    {currentIndex <
                                        TABS.length -
                                        1 ? (
                                        <Button
                                            type="button"
                                            onClick={() =>
                                                handleTabChange(
                                                    TABS[
                                                        currentIndex +
                                                        1
                                                    ].id,
                                                )
                                            }
                                            disabled={
                                                isSubmitting
                                            }
                                        >
                                            Next
                                        </Button>
                                    ) : isAdmin ? (
                                        <Button
                                            type="button"
                                            className="border ms-1 btn-custom-success"
                                            onClick={
                                                formikHandleSubmit
                                            }
                                            disabled={
                                                isSubmitting
                                            }
                                        >
                                            {isSubmitting
                                                ? 'Saving...'
                                                : 'Save'}
                                        </Button>
                                    ) : (
                                        <NavLink
                                            to={buildCompanyUrl(
                                                '/reviews/list',
                                            )}
                                            className="btn border ms-1 btn-custom-success"
                                        >
                                            Return
                                        </NavLink>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            )}
        </Formik>
    );
};

export default ReviewForm;